import { FC } from 'react';
import dayjs from 'dayjs';
import { IActivity as Activity } from '@/utils/activity-calendar/interfaces/IActivity';
import { IAnchor as Anchor } from '@/utils/activity-calendar/interfaces/IAnchor';
import { ActivityStatus } from '@/utils/activity-calendar/enums/ActivityCalendar.enum';
import { DAY_NAMES, DAYS_PER_WEEK, getTypeMeta } from '@/utils/activity-calendar/constants';
import { anchorToDayjs, dayKey, getRecordPath, sortByType, weekStart } from '@/utils/activity-calendar/services/activityCalendarUtils';

interface IProps {
  idx: Record<string, Activity[]>;
  anchor: Anchor;
  today: dayjs.Dayjs;
  isMobile: boolean;
  onActivityClick: (activity: Activity) => void;
}

const ActivityCard: FC<{ activity: Activity; onClick: (() => void) | undefined }> = ({ activity, onClick }) => {
  const t = getTypeMeta(activity.type);
  const planned = activity.status === ActivityStatus.Planned;

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => (e.key === 'Enter' || e.key === ' ') && onClick() : undefined}
      className="flex min-w-0 flex-col gap-[3px] rounded-[7px] border bg-white px-[8px] py-[6px] transition-shadow"
      style={{
        borderColor: '#E5E7EB',
        borderLeft: `3px solid ${t.color}`,
        cursor: onClick ? 'pointer' : 'default',
      }}
      onMouseEnter={onClick ? (e) => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 8px rgb(0 0 0 / 0.10)'; } : undefined}
      onMouseLeave={onClick ? (e) => { (e.currentTarget as HTMLDivElement).style.boxShadow = ''; } : undefined}
    >
      <div className="flex min-w-0 items-start gap-[6px]">
        <span className="mt-[4px] h-[7px] w-[7px] flex-[0_0_auto] rounded-full" style={{ background: t.color }} />
        <span className="min-w-0 text-[11.5px] font-bold leading-[1.25] text-[#1F2937]">{activity.title}</span>
      </div>
      <div className="overflow-hidden text-ellipsis whitespace-nowrap text-[10px] text-[#6B7280]">
        {activity.farm} · {activity.block}
      </div>
      <span
        className="max-w-full self-start whitespace-nowrap rounded-full px-[7px] py-[2px] text-[9px] font-bold uppercase tracking-[0.04em]"
        style={{
          color: planned ? '#e29200' : '#2d855f',
          background: planned ? '#fdf3dc' : '#e3f1ea',
        }}
      >
        {planned ? 'Planned' : 'Completed'}
      </span>
    </div>
  );
};

const EmptyDay: FC = () => (
  <div className="py-[16px] text-center text-[11px] text-[#9CA3AF]">—</div>
);

export const WeekView: FC<IProps> = ({ idx, anchor, today, isMobile, onActivityClick }) => {
  const start = weekStart(anchorToDayjs(anchor));
  const days = Array.from({ length: DAYS_PER_WEEK }, (_, i) => start.add(i, 'day'));

  const cardClick = (a: Activity) => {
    const path = getRecordPath(a);
    return path ? () => onActivityClick(a) : undefined;
  };

  if (isMobile) {
    return (
      <div className="flex flex-col gap-[10px]">
        {days.map((date, dayIndex) => {
          const acts = sortByType(idx[dayKey(date)] || []);
          const isToday = date.isSame(today, 'day');
          return (
            <div key={dayKey(date)} className="overflow-hidden rounded-[10px] border" style={{ borderColor: '#E5E7EB' }}>
              <div
                className="flex items-center gap-[8px] border-b px-[12px] py-[8px]"
                style={{ borderColor: '#E5E7EB', background: isToday ? '#F4FAF6' : 'transparent' }}
              >
                <span
                  className="text-[10.5px] font-bold uppercase tracking-[0.06em]"
                  style={{ color: isToday ? '#2D855F' : '#6B7280' }}
                >
                  {DAY_NAMES[dayIndex]}
                </span>
                <span
                  className="font-display text-[17px] font-extrabold leading-[1.15]"
                  style={{ color: isToday ? '#2D855F' : '#1F2937' }}
                >
                  {date.date()}
                </span>
              </div>
              <div className="flex flex-col gap-[7px] p-[10px]">
                {acts.length ? acts.map((a) => <ActivityCard key={a.id} activity={a} onClick={cardClick(a)} />) : <EmptyDay />}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto pb-[2px]">
      <div
        className="grid min-w-min gap-[10px]"
        style={{ gridTemplateColumns: `repeat(${DAYS_PER_WEEK}, minmax(118px, 1fr))` }}
      >
        {days.map((date, dayIndex) => {
          const acts = sortByType(idx[dayKey(date)] || []);
          const isToday = date.isSame(today, 'day');
          return (
            <div
              key={dayKey(date)}
              className="flex min-w-0 flex-col overflow-hidden rounded-[10px] border bg-white"
              style={{ borderColor: '#E5E7EB' }}
            >
              <div
                className="border-b px-[6px] py-[8px] text-center"
                style={{ borderColor: '#E5E7EB', background: isToday ? '#F4FAF6' : 'transparent' }}
              >
                <div
                  className="text-[10.5px] font-bold uppercase tracking-[0.06em]"
                  style={{ color: isToday ? '#2D855F' : '#6B7280' }}
                >
                  {DAY_NAMES[dayIndex]}
                </div>
                <div
                  className="font-display text-[17px] font-extrabold leading-[1.15]"
                  style={{ color: isToday ? '#2D855F' : '#1F2937' }}
                >
                  {date.date()}
                </div>
              </div>
              <div className="flex max-h-[380px] min-h-[200px] flex-col gap-[7px] overflow-y-auto p-[7px]">
                {acts.length ? acts.map((a) => <ActivityCard key={a.id} activity={a} onClick={cardClick(a)} />) : <EmptyDay />}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
